import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import './OddsComparison.css';

interface FixedPrice {
  bookmaker: string;
  price: number | null;
  movement?: number | null;
}

interface OddsRunner {
  number: number | null;
  horse_name: string;
  best_fixed_price: number | null;
  best_fixed_book: string | null;
  n_fixed_books: number;
  tote_win: number | null;
  exchange_back: number | null;
  exchange_lay: number | null;
  is_scratched: boolean | null;
  fixed_prices: FixedPrice[];
}

interface OddsResponse {
  found: boolean;
  source?: string;
  meeting_name?: string;
  race_number?: number;
  race_name?: string;
  scraped_at?: string;
  url?: string;
  bookmakers: string[];
  runners: OddsRunner[];
}

interface Props {
  meetingName: string;
  raceNumber: string | number;
  date?: string;
}

const fmt = (v: number | null | undefined) =>
  v != null && Number.isFinite(v) ? Number(v).toFixed(2) : '—';

// Pretty bookmaker labels for the column headers.
const BOOK_LABELS: Record<string, string> = {
  ladbrokes: 'Ladbrokes', sportsbet: 'Sportsbet', neds: 'Neds', tabtouch: 'TABtouch',
  nswtab: 'TAB', stab: 'SA TAB', ubet: 'UBET', unibet: 'Unibet', betr: 'betr',
  picklebet: 'Picklebet', pointsbet: 'PointsBet', colossalbet: 'Colossalbet',
  crownbet: 'CrownBet', dabble: 'Dabble', racenetstandard: 'Racenet',
};
const bookLabel = (b: string) => BOOK_LABELS[b] || b;

const OddsComparison: React.FC<Props> = ({ meetingName, raceNumber, date }) => {
  const [data, setData] = useState<OddsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const q = date ? `?date=${encodeURIComponent(date)}` : '';
    fetch(`${API_BASE_URL}/api/oddscom/${encodeURIComponent(meetingName)}/${raceNumber}${q}`)
      .then((r) => r.json())
      .then((j) => { if (!cancelled) setData(j); })
      .catch(() => { if (!cancelled) setData({ found: false, bookmakers: [], runners: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [meetingName, raceNumber, date]);

  if (loading) {
    return <div className="oddscom-panel"><div className="oddscom-head"><h3>Bookmaker Odds</h3><span className="oddscom-muted">Loading…</span></div></div>;
  }
  if (!data || !data.found || data.runners.length === 0) {
    return (
      <div className="oddscom-panel">
        <div className="oddscom-head"><h3>Bookmaker Odds <span className="oddscom-src">odds.com.au</span></h3></div>
        <p className="oddscom-muted">No odds.com.au prices for this race yet. Run <code>oddscom_scraper.py</code> to populate.</p>
      </div>
    );
  }

  const books = data.bookmakers;
  const priceFor = (r: OddsRunner, book: string) => {
    const fp = r.fixed_prices.find((p) => p.bookmaker === book);
    return fp ? fp.price : null;
  };

  return (
    <div className="oddscom-panel">
      <div className="oddscom-head">
        <h3>
          Bookmaker Odds <span className="oddscom-src">odds.com.au</span>
          <span className="oddscom-count"> · {books.length} bookmakers</span>
        </h3>
        <div className="oddscom-meta">
          {data.scraped_at && <span className="oddscom-muted">scraped {new Date(data.scraped_at).toLocaleTimeString()}</span>}
          {data.url && <a href={data.url} target="_blank" rel="noreferrer" className="oddscom-link">source ↗</a>}
          <button className="oddscom-toggle" onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'Show'}</button>
        </div>
      </div>

      {open && (
        <div className="oddscom-table-wrap">
          <table className="oddscom-table">
            <thead>
              <tr>
                <th className="oc-sticky oc-no">#</th>
                <th className="oc-sticky oc-runner">Runner</th>
                <th className="oc-best">Best</th>
                <th>Tote</th>
                <th>BF Back</th>
                <th>BF Lay</th>
                {books.map((b) => <th key={b} className="oc-book">{bookLabel(b)}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.runners.map((r) => (
                <tr key={`${r.number}-${r.horse_name}`} className={r.is_scratched ? 'oc-scratched' : ''}>
                  <td className="oc-sticky oc-no">{r.number ?? '—'}</td>
                  <td className="oc-sticky oc-runner">{r.horse_name}</td>
                  <td className="oc-best">
                    <strong>{fmt(r.best_fixed_price)}</strong>
                    {r.best_fixed_book && <span className="oc-book-tag">{bookLabel(r.best_fixed_book)}</span>}
                  </td>
                  <td>{fmt(r.tote_win)}</td>
                  <td>{fmt(r.exchange_back)}</td>
                  <td>{fmt(r.exchange_lay)}</td>
                  {books.map((b) => {
                    const p = priceFor(r, b);
                    const isBest = p != null && r.best_fixed_price != null && p === r.best_fixed_price;
                    return <td key={b} className={isBest ? 'oc-isbest' : ''}>{fmt(p)}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OddsComparison;
